import { useState, useEffect, useCallback, useRef } from 'react';

import NavDropdown from 'react-bootstrap/NavDropdown';
import DynaLoader from './DynaLoader';
import Button from 'react-bootstrap/Button';

import { Wallet2 } from 'react-bootstrap-icons';

import { fetchCachedAccountDetails, clearCachedAccountDetails, subscribeToCachedAccount } from '../utils/LocalStorage';
import { handleError } from '../utils/ErrorHandler';
import * as Errors from '../utils/ErrorMessages';
import { DynaStripesCurrentNetworkExplorerUrl } from '../utils/Constants';
import matic from '../images/polygon.svg';
import { fetchAccountDetails, fetchConnectedAddress, getInjectedProvider, getProvider, requestAccountSelection, resetProvider } from '../utils/BlockchainAPI';
import {
  discoverWallets,
  subscribeToWallets,
  setSelectedWalletRdns,
} from '../utils/WalletProviders';
import type { AccountDetails } from '../utils/AccountDetails';
import { useConnectWallet } from './WalletSelectionProvider';

const DynaNavLoginDropdown = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const [isWalletInstalled, setIsWalletInstalled] = useState<boolean | undefined>(undefined);
  const [accountEthAddress, setAccountEthAddress] = useState("");
  const [accountEthBalance, setAccountEthBalance] = useState("");
  const [explorerUrl, setExplorerUrl] = useState("");
  const [walletCount, setWalletCount] = useState(0);

  // The full address, so the accountsChanged handler compares like for like.
  const connectedAddressRef = useRef<string>("");

  const connect = useConnectWallet();

  const updateAccountDetails = useCallback((accountDetails: AccountDetails) => {
    connectedAddressRef.current = accountDetails.fullAddress;
    setIsLoading(false);
    setIsWalletInstalled(true);
    setIsWalletConnected(true);
    setAccountEthAddress(accountDetails.shortenedAddress);
    setAccountEthBalance(accountDetails.displayBalance.toString());
    setExplorerUrl(DynaStripesCurrentNetworkExplorerUrl + "address/" + accountDetails.fullAddress);
  }, []);

  const clearAccountDetails = useCallback(() => {
    connectedAddressRef.current = "";
    setIsLoading(false);
    setIsWalletConnected(false);
    setAccountEthAddress("");
    setAccountEthBalance("");
    setExplorerUrl("");
  }, []);

  /** Refresh without prompting -- used after account/chain changes. */
  const refreshQuietly = useCallback(async () => {
    setIsLoading(true);
    try {
      updateAccountDetails(await fetchAccountDetails(false));
    } catch (err: any) {
      console.log("[DynaNavLoginDropdown] Not connected: " + err?.message);
      clearAccountDetails();
    }
  }, [clearAccountDetails, updateAccountDetails]);

  const connectWallet = useCallback(async () => {
    setIsLoading(true);
    try {
      const accountDetails = await connect();
      if (accountDetails) {
        updateAccountDetails(accountDetails);
      } else {
        clearAccountDetails();
      }
    } catch (err: any) {
      clearAccountDetails();
      setIsWalletInstalled(err?.message !== Errors.DS_NO_ETH_WALLET);
      handleError(err);
    }
  }, [clearAccountDetails, connect, updateAccountDetails]);

  // Discover wallets up front so we show "Connect wallet" vs "Install MetaMask"
  // correctly, and restore any cached session.
  useEffect(() => {
    let cancelled = false;

    const unsubscribe = subscribeToWallets((wallets) => {
      if (!cancelled) {
        setWalletCount(wallets.length);
        setIsWalletInstalled(true);
      }
    });

    const restoreSession = async () => {
      const wallets = await discoverWallets();
      if (cancelled) {
        return;
      }
      setWalletCount(wallets.length);
      setIsWalletInstalled(wallets.length > 0 || (window as any).ethereum != null);

      const cachedDetails = fetchCachedAccountDetails();
      if (!cachedDetails) {
        clearAccountDetails();
        return;
      }

      // Show the cached account immediately, then confirm it against the
      // wallet -- the user may have switched accounts since the last visit.
      updateAccountDetails(cachedDetails);

      const currentAddress = await fetchConnectedAddress();
      if (cancelled) {
        return;
      }

      if (currentAddress === null) {
        clearCachedAccountDetails();
        clearAccountDetails();
      } else if (currentAddress.toLowerCase() !== cachedDetails.fullAddress.toLowerCase()) {
        console.log("[DynaNavLoginDropdown] Wallet account changed while away, refreshing");
        clearCachedAccountDetails();
        refreshQuietly();
      }
    };

    restoreSession();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [clearAccountDetails, updateAccountDetails, refreshQuietly]);

  // Follow the shared connected account, so connecting or disconnecting from
  // anywhere else in the app (the mint button) is reflected here too.
  useEffect(() => subscribeToCachedAccount((accountDetails) => {
    if (accountDetails) {
      updateAccountDetails(accountDetails);
    } else {
      clearAccountDetails();
    }
  }), [updateAccountDetails, clearAccountDetails]);

  // Wallet events come from the selected provider, not window.ethereum.
  useEffect(() => {
    if (!isWalletConnected) {
      return;
    }

    let cancelled = false;
    let listening: any = null;

    const handleAccountsChanged = (accounts: string[]) => {
      console.log("[DynaNavLoginDropdown] Accounts changed to " + accounts);

      clearCachedAccountDetails();

      if (accounts.length === 0) {
        clearAccountDetails();
        return;
      }
      if (accounts[0].toLowerCase() === connectedAddressRef.current.toLowerCase()) {
        return;
      }
      refreshQuietly();
    };

    const handleChainChanged = (chainId: string) => {
      console.log("[DynaNavLoginDropdown] Chain changed to " + chainId);
      // The cached balance belongs to the old chain.
      clearCachedAccountDetails();
      resetProvider();
      refreshQuietly();
    };

    const attach = async () => {
      try {
        // Ensures a provider exists even when the session came from cache.
        await getProvider();
      } catch {
        return;
      }
      const provider = getInjectedProvider() as any;
      if (cancelled || !provider?.on) {
        return;
      }
      listening = provider;
      provider.on('accountsChanged', handleAccountsChanged);
      provider.on('chainChanged', handleChainChanged);
      console.log("[DynaNavLoginDropdown] Listening for wallet account changes");
    };

    attach();

    return () => {
      cancelled = true;
      listening?.removeListener?.('accountsChanged', handleAccountsChanged);
      listening?.removeListener?.('chainChanged', handleChainChanged);
    };
  }, [isWalletConnected, clearAccountDetails, refreshQuietly]);

  const disconnectWallet = () => {
    console.log("Disconnecting wallet..");
    clearCachedAccountDetails();
    clearAccountDetails();
  };

  const refreshWallet = async () => {
    clearCachedAccountDetails();
    setIsLoading(true);
    try {
      updateAccountDetails(await fetchAccountDetails());
    } catch (err: any) {
      clearAccountDetails();
      handleError(err);
    }
  };

  const switchAccount = async () => {
    setIsLoading(true);
    try {
      await requestAccountSelection();
      clearCachedAccountDetails();
      updateAccountDetails(await fetchAccountDetails());
    } catch (err: any) {
      setIsLoading(false);
      handleError(err);
    }
  };

  const switchWallet = () => {
    clearCachedAccountDetails();
    setSelectedWalletRdns(null);
    resetProvider();
    clearAccountDetails();
    // connectWallet() opens the picker itself whenever several are installed.
    connectWallet();
  };

  if (isLoading) {
    return <DynaLoader variant="small" />;
  }

  if (isWalletInstalled === false) {
    return (
      <Button target="_blank" rel="noreferrer" href="https://metamask.io">Install MetaMask</Button>
    );
  }

  if (!isWalletConnected) {
    return <Button onClick={connectWallet}>Connect wallet</Button>;
  }

  return (
    <NavDropdown title="Your Details" id="basic-nav-dropdown">
      <NavDropdown.Item href={explorerUrl} target="_blank" rel="noreferrer"><Wallet2 className='navDropdownIcon' />{accountEthAddress}</NavDropdown.Item>
      <NavDropdown.Item href={explorerUrl} target="_blank" rel="noreferrer"><img src={matic} alt="matic logo" className='navDropdownIcon' />{accountEthBalance}</NavDropdown.Item>
      <NavDropdown.Divider />
      <NavDropdown.Item onClick={refreshWallet}>Refresh</NavDropdown.Item>
      <NavDropdown.Item onClick={switchAccount}>Switch account</NavDropdown.Item>
      {walletCount > 1 && (
        <NavDropdown.Item onClick={switchWallet}>Switch wallet</NavDropdown.Item>
      )}
      <NavDropdown.Item onClick={disconnectWallet}>Disconnect</NavDropdown.Item>
    </NavDropdown>
  );
}

export default DynaNavLoginDropdown;
